import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import axios from '@axios';
import { RootState } from '@redux/reducers';
import { Paginated } from '@models/paginated';
import { toQueryParameters } from 'src/extensions/object';
import { PaginatedResponseApi } from '@models/order/items/response';
import { pendingOrdersReportColumnsTranslations } from '@utils/translation';
import {
    PendingOrderResponse,
    PendingOrderResponseApi,
    ReportRequest,
} from '@models/report';
import { exportReport } from '@utils/file';
import { translate } from '@components/i18n';

export const fetchPendingOrders = createAsyncThunk<
    Paginated<PendingOrderResponse>,
    ReportRequest
>('pending-orders/fetchPendingOrders', async (request, thunkAPI) => {
    try {
        const state = thunkAPI.getState() as RootState;
        const { pendingOrders } = state;
        const { request: oldRequest } = pendingOrders;

        thunkAPI.dispatch(updateRequestPendingOrder(request));

        const params = generateParams(request, oldRequest);

        const response = await axios.get<
            PaginatedResponseApi<PendingOrderResponseApi>
        >(`/relatorio/pedidospendentes?${params}`);
        const { data: dataResponse, status: statusResponse } = response;

        if (!dataResponse) {
            return {
                items: [],
                current: 0,
                total: 0,
                pageSize: 0,
            };
        } else {
            const result = dataResponse.content.map((u) =>
                Object.assign(new PendingOrderResponseApi(), u),
            );

            if (statusResponse === 200) {
                return {
                    items: result.map((u) => u.toPendingOrderResponse()),
                    current: request.current,
                    total: response.data.totalElements,
                    pageSize: request.pageSize,
                };
            } else {
                return thunkAPI.rejectWithValue('');
            }
        }
    } catch (e) {
        const { response } = e;
        const { data } = response;
        const { error } = data;
        const { message } = error;

        return thunkAPI.rejectWithValue(message);
    }
});

export const exportPendingOrders = createAsyncThunk(
    'pending-orders/exportPendingOrders',
    async (request: ReportRequest, thunkAPI) => {
        try {
            const state = thunkAPI.getState() as RootState;
            const { pendingOrders } = state;
            const { request: oldRequest } = pendingOrders;

            const params = generateParams(request, oldRequest);

            const response = await axios.get(
                `/relatorio/pedidospendentes/export?${params}`,
                { responseType: 'blob' },
            );
            exportReport(
                response.data,
                `${translate('general.reportPendingOrders')}.xlsx`,
            );
        } catch (e) {
            const { response } = e;
            const { data } = response;
            const { error } = data;
            const { message } = error;

            return thunkAPI.rejectWithValue(message);
        }
    },
);

const generateParams = (request: ReportRequest, oldRequest: ReportRequest) => {
    const { filterValue, filterBy } = oldRequest;

    if (filterValue != request.filterValue || filterBy != request.filterBy)
        request.current = 1;

    let parameters: any = {
        page: request.current - 1,
        size: request.pageSize,
        sort: request.field
            ? `${pendingOrdersReportColumnsTranslations[request.field] || ''},${
                  request.order === 'ascend' ? 'asc' : 'desc'
              }`
            : '',
    };

    parameters =
        request.filterValue && request.filterBy && request.comparisonType
            ? {
                  ...parameters,
                  filterValue: request.filterValue,
                  filterType: request.filterBy?.toUpperCase(),
                  comparisonType: request.comparisonType?.toUpperCase(),
              }
            : parameters;

    return toQueryParameters(parameters);
};

interface IPendingOrderSelectorState {
    pendingOrder: PendingOrderResponse;
    pendingOrders: PendingOrderResponse[];
    request: ReportRequest;
    isFetching: boolean;
    isSuccess: boolean;
    isError: boolean;
    errorMessage: string;
}

const initialState: IPendingOrderSelectorState = {
    pendingOrder: undefined,
    pendingOrders: [],
    request: {
        filterBy: '',
        filterValue: '',
        comparisonType: '',
        status: '',
        pageSize: 10,
        current: 1,
        total: 0,
    },
    isFetching: true,
    isSuccess: false,
    isError: false,
    errorMessage: '',
};

const pendingOrdersSlice = createSlice({
    name: 'pendingOrdersSlice',
    initialState,
    reducers: {
        pendingOrders: (state, action: PayloadAction<PendingOrderResponse>) => {
            state.pendingOrder = action.payload;

            return state;
        },
        updateRequestPendingOrder: (
            state,
            action: PayloadAction<ReportRequest>,
        ) => {
            state.request = action.payload;

            return state;
        },
        resetPendingOrderState: (state) => {
            state = { ...initialState };
            return state;
        },
    },
    extraReducers: {
        [fetchPendingOrders.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<Paginated<PendingOrderResponse>>,
        ) => {
            state.pendingOrders = [...payload.items];
            state.request = {
                ...state.request,
                total: payload.total,
                pageSize: payload.pageSize,
                current: payload.current,
            };
            state.isFetching = false;
            state.isSuccess = true;
            return state;
        },
        [fetchPendingOrders.rejected.toString()]: (state, action) => {
            state.isFetching = false;
            state.isError = true;
            state.errorMessage = action.payload;
        },
        [fetchPendingOrders.pending.toString()]: (state) => {
            state.isFetching = true;
            state.isError = false;
        },
    },
});

export const pendingOrderSelector = (
    state: RootState,
): IPendingOrderSelectorState => state.pendingOrders;

export const {
    pendingOrders,
    updateRequestPendingOrder,
    resetPendingOrderState,
} = pendingOrdersSlice.actions;

export default pendingOrdersSlice.reducer;
